import React, { forwardRef } from "react";
import { Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { numberToPersianWords } from "../../../utils/currency-formater";
import { useLangContext } from "../../../context/langContext";

type ChangeHandler = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => void;

export interface IMuiAutoCompleteProps
  extends Omit<TextFieldProps, "onChange"> {
  isCurrency?: boolean;
  value?: string | number;
  onChange?: ChangeHandler;
  example?: React.ReactNode;
  formatter?: (v: any) => any;
  reverseFormatter?: (v: any) => any;
}

const MuiAutoComplete = forwardRef<HTMLInputElement, IMuiAutoCompleteProps>(
  (
    {
      sx,
      value,
      label,
      error,
      onBlur,
      example,
      required,
      disabled,
      onChange,
      formatter,
      multiline,
      isCurrency,
      placeholder,
      type = "text",
      reverseFormatter,
      inputMode = "text",
      InputProps: incomingInputProps,
      ...rest
    },
    ref
  ) => {
    const { dir } = useLangContext();
    const [displayVal, setDisplayVal] = React.useState("");

    const isPassword = type === "password";

    const inputValue =
      (incomingInputProps && (incomingInputProps as any).value) ??
      (formatter ? formatter(value) : value ?? "");

    const mergedInputProps = {
      ...(incomingInputProps as any),
    };

    const shouldUseInternalOnChange = !incomingInputProps;

    return (
      <Stack direction="column" width="100%">
        {label && (
          <Typography
            variant="labelMediumBold"
            color={(theme) => theme.palette.dominant[400]}
          >
            {label}
          </Typography>
        )}

        <TextField
          {...rest}
          type={"text"}
          onBlur={onBlur}
          name={rest.name}
          value={inputValue}
          required={required}
          disabled={disabled}
          multiline={multiline}
          inputMode={inputMode}
          placeholder={placeholder}
          minRows={multiline ? 4 : 1}
          inputRef={(rest as any).inputRef || ref}
          onChange={
            shouldUseInternalOnChange
              ? (
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => {
                  const raw = e.target.value;
                  const val = reverseFormatter ? reverseFormatter(raw) : raw;
                  if (isCurrency && /^\d*\.?\d*$/.test(String(val)))
                    setDisplayVal(numberToPersianWords(String(val)));
                  (e.target as HTMLInputElement).value = val as any;
                  onChange && onChange(e);
                }
              : undefined
          }
          error={Boolean(error)}
          helperText={
            error
              ? error
              : example
              ? example
              : isCurrency
              ? displayVal
              : undefined
          }
          dir={isPassword || isCurrency ? "ltr" : dir}
          InputProps={{
            ...mergedInputProps,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              padding: "3px",
              "& .MuiInputAdornment-root": {
                margin: 0,
                padding: 0,
              },
              "& .MuiOutlinedInput-input:focus": {
                outline: "none !important",
              },
            },
            "& input::placeholder": {
              direction: isCurrency ? "ltr" : dir,
              textAlign: isCurrency ? "left" : "right",
            },
            ...sx,
          }}
        />
      </Stack>
    );
  }
);

export default MuiAutoComplete;
